import { GoogleOAuthProvider, useGoogleLogin, googleLogout } from '@react-oauth/google';
import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';

import Spinner from '@/src/components/common/Elements/Spinner/Spinner';
import {
  GoogleDrivePicker,
  PickerCustomEvent,
} from '@/src/components/common/GoogleDrivePicker/GoogleDrivePicker';
import ConfirmModal from '@/src/components/common/Modal/ConfirmModal/ConfirmModal';
import ModalPortal from '@/src/components/common/Modal/ModalPortal';
import { showToast } from '@/src/globalFunction';
import { useToggle } from '@/src/hooks/useToggle';
import { checkAccess, INTERNAL_USER_ROLES } from '@/src/lib/authorization/authorization';
import { RootState } from '@/src/reducers/types';

import { useConnectGoogleDrive } from './api/connectGoogleDrive';
import { useConnectGoogleDriveCallback } from './api/connectGoogleDriveCallback';
import { useDisconnectGoogleDrive } from './api/disconnectGoogleDrive';
import { useSelectGoogleDriveFolder } from './api/selectGoogleDriveFolder';
import { GoogleDriveAccountCard } from './GoogleDriveAccountCard';

import { AppConfigType } from '../../AppList/AppList';
import { useConfirmDisconnect } from '../../useConfirmDisconnect';
import AppCard from '../AppCard';

export type BaseApp = {
  name: string;
  shortName: string;
  projectId: number;
};

export type GoogleDriveFolderItem = {
  accountId: number;
  createdAt: string;
  folderId: string;
  folderName: string;
  id: number;
  updatedAt: string;
  userId: number;
};

export type GoogleDriveItem = {
  connectedOn: string;
  driveChangesToken: string;
  email: string;
  googleDriveFolders: GoogleDriveFolderItem[];
  id: number;
  projectId: number;
  updatedAt: string;
  userId: number;
};

type GoogleDriveCardProps = {
  app: GoogleDriveItem[];
  appConfig: AppConfigType;
  projectId: number;
  onDisconnect: () => void;
  onRefresh: () => void;
};

const GoogleDriveCard = ({
  appConfig,
  app: accounts,
  projectId,
  onRefresh,
  onDisconnect,
}: GoogleDriveCardProps) => {
  const [showConnectModal, toggleShowConnectModal] = useToggle(false);
  const [selectedAccessToken, setSelectedAccessToken] = useState<string | null>(null);
  const loggedUser = useSelector((store: RootState) => store.loggedUser);
  const [DisconnectModal, confirmDisconnect] = useConfirmDisconnect({
    message:
      'You are about to disconnect your Google Drive Account. Newly uploaded files will no longer sync to Inbox. Do you wish to proceed?',
  });
  const disconnectMutation = useDisconnectGoogleDrive();
  const selectFolderMutation = useSelectGoogleDriveFolder();
  const connectGoogleDriveMutation = useConnectGoogleDrive({
    projectId,
    config: {
      enabled: false,
    },
  });
  const encryptedState = connectGoogleDriveMutation.data?.state;
  const connectGoogleDriveCallbackMutation = useConnectGoogleDriveCallback();

  const login = useGoogleLogin({
    flow: 'auth-code',
    onSuccess: async (tokenResponse) => {
      console.log('Google Login Success:', tokenResponse);

      try {
        const response = await connectGoogleDriveCallbackMutation.mutateAsync({
          state: encryptedState,
          code: tokenResponse.code,
        });

        if (!response.accessToken) {
          console.log('Missing access token');
          toggleShowConnectModal(false);
          showToast('error', 'Something went wrong. Please try again later.');

          return;
        }

        setSelectedAccessToken(response.accessToken);
        toggleShowConnectModal(false);
      } catch (error) {
        console.log('Callback failed', { error });
        toggleShowConnectModal(false);
        showToast('error', 'Something went wrong. Please try again later.');
      }
    },
    onError: (errorResponse) => {
      console.log('Google Login Failed', { errorResponse });

      toggleShowConnectModal(false);
      showToast('error', 'Something went wrong. Please try again later.');
    },
    scope:
      'https://www.googleapis.com/auth/drive.readonly https://www.googleapis.com/auth/userinfo.email openid',
  });

  // Hide connect button if logged  user already connected
  const showConnectButton = useMemo(
    () => accounts?.findIndex((account) => account.userId === loggedUser.data.id) < 0,
    [accounts, loggedUser]
  );

  const handleDisconnect = useCallback(
    async (userId: number) => {
      const response = await disconnectMutation.mutateAsync({
        projectId: +projectId,
        userId: userId,
      });

      if (response && onDisconnect) {
        onDisconnect();
      }

      return response;
    },
    [projectId, disconnectMutation, onDisconnect]
  );

  const handleConfirmDisconnect = useCallback(
    async (userId: number) => {
      if (!(await confirmDisconnect())) {
        return;
      }

      googleLogout();
      handleDisconnect(userId);
    },
    [confirmDisconnect, handleDisconnect]
  );

  const handleConnect = async () => {
    // if we need to skip if tere is already data, add condition
    if (!connectGoogleDriveMutation.data?.state) {
      const response = await connectGoogleDriveMutation.refetch();

      if (response.status !== 'success') {
        showToast('error', 'Something went wrong. Please try again later.');
        return;
      }
    }

    login();
  };

  const handleSelectFolderEvent = async (e: PickerCustomEvent) => {
    console.log('handleSelectFolderEvent', { e });
    const selectedFolder = e.detail.docs[0];

    if (!selectedFolder) {
      showToast('error', 'Something went wrong when seleting a folder from google drive picker');
    }

    try {
      await selectFolderMutation.mutateAsync({
        projectId,
        folderId: selectedFolder.id,
        folderName: selectedFolder.name,
      });

      await onRefresh();

      setSelectedAccessToken(null);

      showToast('success', `${selectedFolder.name} has been successfully connected`);
    } catch (error) {
      console.log(error);
      if (error.response?.data) {
        showToast('error', `${error.response.data}`);
      } else {
        showToast('error', 'Something went wrong. Please try again later.');
      }
    }
  };

  return (
    <React.Fragment>
      {(disconnectMutation.isLoading ||
        connectGoogleDriveCallbackMutation.isLoading ||
        selectFolderMutation.isLoading) && <Spinner />}
      {selectedAccessToken && (
        <GoogleDrivePicker accessToken={selectedAccessToken} onSelect={handleSelectFolderEvent} />
      )}

      <AppCard
        appConfig={appConfig}
        onConnect={toggleShowConnectModal}
        isConnected={false} // We force false here since each disconnect is per folder listed below
        showConnectButton={showConnectButton}
      >
        {!!accounts?.length && (
          <div className="apps-card-body">
            {accounts.map((account) => {
              const googleDriveFolder = account.googleDriveFolders[0];

              return (
                <GoogleDriveAccountCard
                  key={googleDriveFolder.folderId}
                  account={googleDriveFolder}
                  connectedOn={account.connectedOn}
                  email={account.email}
                  userId={account.userId}
                  onSelectAccessToken={setSelectedAccessToken}
                  onDisconnect={handleConfirmDisconnect}
                  className="first:tw-pt-0 tw-pt-[20px]"
                />
              );
            })}
          </div>
        )}
      </AppCard>

      {showConnectModal && (
        <ModalPortal>
          <ConfirmModal
            onCancel={toggleShowConnectModal}
            onConfirm={handleConnect}
            confirmBtn={{
              label: `Connect to ${appConfig.shortName}`,
            }}
            title="Connect your account with your Google Drive"
            confirmLabel="Connect to Google Drive"
            classes="app-settings-modal"
          >
            <div className="mainContainer">
              <div className="boxContainer">
                <div
                  className="logoContainer"
                  style={{
                    backgroundImage: `url(/images/cloudcfo-logo-colored-icon-above-text.png)`,
                  }}
                ></div>
              </div>
              <div className="dashedLine"></div>
              <div className="boxContainer">
                <div
                  className="logoContainer"
                  style={{ backgroundImage: `url(/images/gdrive-icon.png)` }}
                ></div>
              </div>
            </div>

            <div className="appSettingsContent">
              <p>How to connect</p>
              <ol>
                <li>
                  Click the <strong>Connect to Google Drive button</strong> - you'll be redirected
                  to Google log in.
                </li>
                <li>
                  Once connected, you’ll be selecting Google Drive folder you want to sync with
                  Inbox.
                </li>
              </ol>
            </div>
          </ConfirmModal>
        </ModalPortal>
      )}
      {DisconnectModal && (
        <ModalPortal>
          <DisconnectModal />
        </ModalPortal>
      )}
    </React.Fragment>
  );
};

const withGoogleOAuth = <P extends object>(Component: React.ComponentType<P>) => {
  const WrappedComponent = (props: P) => (
    <GoogleOAuthProvider clientId={process.env.GOOGLE_CLIENT_ID}>
      <Component {...props} />
    </GoogleOAuthProvider>
  );

  WrappedComponent.displayName = `withGoogleOAuth(${
    Component.displayName || Component.name || 'Component'
  })`;

  return WrappedComponent;
};

export default withGoogleOAuth(GoogleDriveCard);
